/* istanbul ignore file */
// Due to the difficulty in testing the apollo link logic, we're skipping testing this file
import { ApolloClient, ApolloLink, from, HttpLink, InMemoryCache, type NextLink, type NormalizedCacheObject, type Operation, split } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition, Observable, omitDeep } from '@apollo/client/utilities';
import { useAuth } from 'components/Auth';
import { OperationTypeNode } from 'graphql';
import { useSnackbar } from 'notistack';
import { useAppConfig } from 'providers/AppConfig';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { snackBarMessageWithSupportId } from '@/TelemetryInit';

export const useApolloClient = () => {
    const config = useAppConfig();
    const { authFetch } = useAuth();
    const { i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const buildClient = (): ApolloClient<NormalizedCacheObject> => {
        const httpLink = new HttpLink({
            uri: config.REACT_APP_GRAPHQL_API_HOST,
            credentials: 'include',
            fetch: authFetch as typeof fetch,
        });
    
        const wsLink = new WebSocketLink({
            uri: config.REACT_APP_GRAPHQL_WS_HOST,
            options: {
                reconnect: true
            }
        });

        const removeTypenameFromMutationLink = new ApolloLink((operation: Operation, forward: NextLink) => {
            const definition = operation?.query?.definitions.filter((def) => def.kind === 'OperationDefinition')?.[0];
            const mutation: OperationTypeNode = OperationTypeNode.MUTATION;
            if (definition?.kind === 'OperationDefinition' && definition?.operation === mutation) {
                operation.variables = omitDeep(operation.variables, '__typename');
                return forward(operation);
            }
            return forward(operation);
        });

        const retryLink = new RetryLink({
            attempts: {
                max: 3,                  // Maximum number of attempts
                retryIf: (error, _operation) => {
                    // Retry if a network error occurs and its HTTP status code is 503,
                    // or if graphQLServiceUnavailableLink flagged a GraphQL 503 error below
                    return !!error && error.statusCode === 503;
                }
            },
            delay: {
                initial: 1000,           // Start waiting 1 second before retrying
                max: Infinity,
                jitter: true             // Randomize delays slightly to prevent server hammering
            }
        });

        // RetryLink only retries on thrown/network errors. A GraphQL error with
        // extensions.StatusCode === 503 can come back two different ways depending on the
        // *transport* HTTP status: as a normal `next` response (transport status < 300), or
        // wrapped in a thrown ServerError with `.result.errors` (transport status >= 300).
        // This link checks both channels and re-throws a tagged error either way, so retryLink
        // (which only inspects `error.statusCode`) sees a consistent shape to retry on.
        const findServiceUnavailableError = (errors?: readonly { extensions?: Record<string, any>, message: string }[]) =>
            errors?.find((error) => error.extensions?.StatusCode === 503);

        const toRetryableError = (serviceUnavailableError: { extensions?: Record<string, any>, message: string }) =>
            Object.assign(
                new Error(serviceUnavailableError.extensions?.message as string || serviceUnavailableError.message),
                { statusCode: 503 }
            );

        const graphQLServiceUnavailableLink = new ApolloLink((operation, forward) => {
            return new Observable((observer) => {
                const subscription = forward(operation).subscribe({
                    next: (response) => {
                        const serviceUnavailableError = findServiceUnavailableError(response.errors);
                        if (serviceUnavailableError) {
                            observer.error(toRetryableError(serviceUnavailableError));
                            return;
                        }
                        observer.next(response);
                    },
                    error: (networkError) => {
                        const serviceUnavailableError = findServiceUnavailableError(networkError?.result?.errors);
                        observer.error(serviceUnavailableError ? toRetryableError(serviceUnavailableError) : networkError);
                    },
                    complete: () => observer.complete(),
                });
                return () => subscription.unsubscribe();
            });
        });

        const httpLinks = from([removeTypenameFromMutationLink, retryLink, graphQLServiceUnavailableLink, httpLink]);

        const link = split(
            // split based on operation type
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                );
            },
            wsLink,
            httpLinks
        );

        const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
            if (!Boolean(operation.getContext().skipGlobalErrorHandling)) {
                if (graphQLErrors) {
                    const graphQLError = graphQLErrors[0];
                    const errorMessage = graphQLError?.extensions?.message  as string || graphQLErrors[0].message;
                    // avoid showing error snackbar for 404 errors or html error messages
                    if ((
                        graphQLError?.extensions?.code !== "RESPONSE_NO_SUCCESS_NotFound" 
                        || graphQLError?.extensions?.StatusCode !== 404
                    ) && !errorMessage?.toLowerCase()?.includes('<html')) {
                        enqueueSnackbar?.(snackBarMessageWithSupportId(errorMessage), { variant: 'error' });
                    } else {
                        console.error(errorMessage);
                    }
    
                    if (graphQLErrors.length > 1) {
                        for (const error of graphQLErrors.slice(1)) {
                            console.error(error.extensions?.message || error.message);
                        }
                    }
                } else if (networkError) {
                    if (networkError?.message?.toLowerCase()?.includes('<html')) {
                        console.error('Unexpected Error:', networkError);
                    } else {
                        enqueueSnackbar?.(snackBarMessageWithSupportId(networkError.message), { variant: 'error' });
                    }
                }
            }
        });
    
        const authLink = setContext((_, { headers }) => {
            // var lang = localStorage.getItem('i18nextLng');
            return {
                headers: {
                    ...headers,
                    'Accept-Language': i18n.language
                } 
            }
            // return token 
            //     ? {
            //         headers: {
            //             ...headers,
            //             Authorization: `Bearer ${token}`,
            //             'Accept-Language': i18n.language
            //         }
            //     }
            //     : { 
            //         headers: {
            //         ...headers,
            //         'Accept-Language': i18n.language
            //         } 
            //     }
        });

        return new ApolloClient({
            link: authLink.concat(errorLink.concat(link)),
            cache: new InMemoryCache({
                typePolicies: {
                    Activity: {
                        keyFields: ["activityId"]
                    },
                    Provider: {
                        keyFields: ["providerId"]
                    },
                    Invoice: {
                        keyFields: ["invoiceId"]
                    }
                }
            })
        });
    }

    const { current: client } = useRef<ApolloClient<NormalizedCacheObject>>(buildClient());

    useEffect(() => {
        client.refetchQueries({
            include: 'active'
        });
    }, [i18n.language]);

    return client;
}